import Alert from '@/components/Alert'
import useDefaultToast from '@/components/useDefaultToast'
import { redirectAutoLogout, removeAuthToken } from '@/lib/api/authToken'
import { destroyCurrentUser } from '@/lib/api/currentUserApi'
import { logoutThunk } from '@/redux/slices/sessionSlice'
import { useAppDispatch } from '@/redux/store'
import { router } from 'expo-router'
import { useCallback, useEffect } from 'react'
import { Platform } from 'react-native'

type DestroyAccountAlertProps = {
  open: boolean
  setOpen: (open: boolean) => void
}

export default function DestroyAccountAlert({ open, setOpen }: DestroyAccountAlertProps) {
  const dispatch = useAppDispatch()
  const toast = useDefaultToast()

  const handleDestroyAccount = useCallback(
    async (password: string, confirmKeyword: string) => {
      if (!confirmKeyword.trim()) {
        toast.show('请输入「确认注销」')
        return
      }

      try {
        const result = await destroyCurrentUser({
          confirmKeyword: confirmKeyword.trim(),
          password: password.trim() || undefined,
        })

        if (result.data?.destroyUser?.errors && result.data.destroyUser.errors.length > 0) {
          const error = result.data.destroyUser.errors[0]
          console.log('error', error)
          toast.show(error?.message || '注销账户失败')
          return
        }

        toast.show('账户已注销')

        if (Platform.OS === 'web') {
          removeAuthToken()
          redirectAutoLogout()
          return
        }
        await dispatch(logoutThunk())
        router.dismissAll()
        router.replace('/login')
      } catch (error) {
        console.error('Failed to destroy account:', error)
        toast.show('注销账户失败，请稍后重试')
      }
    },
    [dispatch, toast]
  )

  const promptForConfirmKeyword = useCallback(
    (password: string) => {
      Alert.prompt(
        '请输入「确认注销」',
        '注销账户后，您的所有数据将被永久删除且无法恢复。',
        [
          { text: '取消', style: 'cancel', onPress: () => setOpen(false) },
          {
            text: '确认注销',
            style: 'destructive',
            onPress: (confirmKeyword?: string) => {
              if (confirmKeyword) {
                handleDestroyAccount(password, confirmKeyword)
              }
              setOpen(false)
            },
          },
        ],
        'plain-text'
      )
    },
    [handleDestroyAccount, setOpen]
  )

  const promptForPassword = useCallback(() => {
    Alert.prompt(
      '注销账户确认',
      '请输入当前账户的密码，并确保你了解注销账户后的结果。',
      [
        { text: '取消', style: 'cancel', onPress: () => setOpen(false) },
        {
          text: '下一步',
          onPress: (password?: string) => {
            promptForConfirmKeyword(password || '')
          },
        },
      ],
      'secure-text'
    )
  }, [promptForConfirmKeyword, setOpen])

  useEffect(() => {
    if (open) {
      promptForPassword()
    }
  }, [open, promptForPassword])

  return null
}
